import clientConfigs from 'app/config/client';
import Cookies, { CookieAttributes } from 'js-cookie';
import { NonCriticalError } from '../../constants/NonCriticalError';
import { getGlobalHistory } from '../../services/history';
import { absurd } from 'fp-ts/lib/function';
import { AppDispatch } from 'redux';
import { ISharedState } from './engoo';
import { ICookieContext } from '.';

export enum CookieContext {
  DMM = 'dmm',
  ENGOO = 'engoo',
  THIRD_PARTY = 'thirdParty',
}

export const fold = <A>(
  onDmm: (dmmContext: CookieContext.DMM) => A,
  onEngoo: (engooContext: CookieContext.ENGOO) => A,
  onThirdParty: (thirdPartyContext: CookieContext.THIRD_PARTY) => A,
) => (cookie: CookieContext): A => {
  switch (cookie) {
    case CookieContext.DMM:
      return onDmm(cookie);
    case CookieContext.ENGOO:
      return onEngoo(cookie);
    case CookieContext.THIRD_PARTY:
      return onThirdParty(cookie);
    default:
      return absurd(cookie);
  }
};

export class CookieMismatchError extends NonCriticalError {
  public constructor() {
    super();
    this.name = 'CookieMismatchError';
  }
}

const getCookieDomain = (context: ICookieContext) => (
  // save the cookie to the brand's domain so that it can still be
  // accessed from the brand's appDomain
  process.env.NODE_ENV === 'development' ?
    getGlobalHistory().location.hostname :
    context.defaultDomain.hostname
);

const applyDefaultCookieOptions = (
  definedOptions: IConfigurableCookieOptions, context: ICookieContext): ISetCookieOptions => {
  // must specify domain associated with the cookie in order to remove it;
  // browsers will also complain about missing same-site values even when clearing,
  // so they must be specified too
  const sameSiteOptions: ISetCookieOptions =
    clientConfigs.environment.insecureSharedCookies
      ? { sameSite: 'lax', secure: false }
      : { sameSite: 'none', secure: true };
  return {
    domain: getCookieDomain(context),
    ...definedOptions,
    ...sameSiteOptions,
  };
};

export const setCookie = ({
  key,
  value,
  options = {},
  context,
}: {
  key: string,
  value: string | ISharedState,
  options?: IConfigurableCookieOptions,
  context: ICookieContext,
}) => {
  /* The value can either be a string or shared state, but js-cookie with automatically stringify
   * the value if it's an object.*/
  Cookies.set(key, value, applyDefaultCookieOptions(options, context));
};

export const clearCookie = ({
  context,
  key,
  options = {},
}: {
  context: ICookieContext;
  key: string;
  options?: IConfigurableCookieOptions,
}) => {
  // must specify domain associated with the cookie in order to remove it
  Cookies.remove(key, applyDefaultCookieOptions(options, context));
};

// https://www.chromestatus.com/feature/5088147346030592
// Chrome is intending to make 'lax' the default option when no SameSite policy is
// specified. For this reason, sameSite is now a required field of ISetCookieOptions.
export interface ISetCookieOptions extends CookieAttributes {
  sameSite: 'strict' | 'lax' | 'none';
}
// The set of configuration that can be specified by a user of the cookie library.
// Excludes secure and sameSite, as these must be set by the environment.
export interface IConfigurableCookieOptions extends Partial<Omit<ISetCookieOptions, 'secure' | 'sameSite'>> {}

export const checkForCookieMismatch = (
    previousValue: string | undefined,
    newValue: string,
  ) =>
    (dispatch: AppDispatch) => {
      // log out if there is an already logged in user but no cookie OR
      // log out if the id previously set in the cookie doesn't match logged in user's id
      if (newValue !== previousValue) {
        return Promise.reject(new CookieMismatchError());
      }
      return Promise.resolve();
    };
